import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * ===========================
 * TokenizeCardPage.js
 * ===========================
 *
 * What this page does:
 * - The user types card details on THIS page only.
 * - We encrypt PAN + CVV in the browser using PayTrace E2EE (encryptValue).
 * - We send ONLY encrypted values back to the caller site via postMessage.
 *
 * PCI safety rules (high level):
 * - Never log raw card data.
 * - Never send raw PAN/CVV back to the caller.
 * - Only postMessage to an exact, validated origin (never "*").
 */

// Local brand logos (UI only)
import visaLogo from "./assets/cards/visa.svg";
import mcLogo from "./assets/cards/mastercard.svg";
import amexLogo from "./assets/cards/amex.svg";
import discoverLogo from "./assets/cards/discover.svg";
import genericLogo from "./assets/cards/generic.svg";

// Producers National logo (UI only)
import { ReactComponent as PncLogo } from "./assets/logo.svg";

/**
 * PayTrace E2EE hosted script URL.
 * (PayTrace provides the encryption function that runs in the browser.)
 */
const PAYTRACE_E2EE_SRC = "https://api.paytrace.com/assets/e2ee/paytrace-e2ee.js";

/**
 * Brand logo mapping for UI
 */
const BRAND_LOGO = {
  VISA: visaLogo,
  MASTERCARD: mcLogo,
  AMEX: amexLogo,
  DISCOVER: discoverLogo,
  CARD: genericLogo,
};

/**
 * Utility: loads a script dynamically (only once).
 * - If the script is already loaded, we resolve immediately.
 * - If it exists but is still loading, we wait for load/error.
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing?.dataset?.loaded === "true") {
      resolve();
      return;
    }

    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;

    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true }
    );

    script.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), {
      once: true,
    });

    document.body.appendChild(script);
  });
}

/**
 * Helper: removes all non-digit characters.
 * We allow spaces for readability in the UI but validate using digits only.
 */
function onlyDigits(str) {
  return (str || "").replaceAll(/\D+/g, "");
}

/**
 * Detect the card brand based on digits entered (BIN/IIN detection).
 * This is UI + validation logic (CVV length + formatting rules).
 */
function detectCardBrand(digits) {
  if (!digits) return "CARD";
  if (digits?.startsWith("4")) return "VISA"; // Visa: starts with 4

  // Mastercard: 51-55 OR 2221-2720
  if (
    /^(5[1-5])/.test(digits) ||
    /^(222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)/.test(digits)
  )
    return "MASTERCARD";

  if (/^3[47]/.test(digits)) return "AMEX"; // Amex: 34 or 37

  // Discover ranges
  if (
    /^(6011)/.test(digits) ||
    /^(65)/.test(digits) ||
    /^(64[4-9])/.test(digits) ||
    /^(622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9([01]\d|2[0-5])))/.test(digits)
  )
    return "DISCOVER";

  return "CARD";
}

/**
 * Luhn check (basic PAN typo detection).
 * This doesn't guarantee a real card; it just catches many mistakes.
 */
function luhnCheck(digits) {
  let sum = 0;
  let alt = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.codePointAt(i) - 48;
    if (n < 0 || n > 9) return false;

    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }

    sum += n;
    alt = !alt;
  }

  return sum % 10 === 0;
}

/**
 * Different brands allow different PAN lengths.
 */
function panLengthOk(brand, len) {
  if (brand === "AMEX") return len === 15;
  if (brand === "MASTERCARD") return len === 16;
  if (brand === "VISA") return len === 13 || len === 16 || len === 19;
  if (brand === "DISCOVER") return len === 16 || len === 19;
  return len >= 13 && len <= 19;
}

/**
 * Card number formatting:
 * - AMEX: 4-6-5
 * - Others: groups of 4
 * Pure UI formatting; we still encrypt/validate using digits only.
 */
function formatCardNumberByBrand(digits, brand) {
  const d = (digits || "").slice(0, 19);

  if (brand === "AMEX") {
    const p1 = d.slice(0, 4);
    const p2 = d.slice(4, 10);
    const p3 = d.slice(10, 15);
    return [p1, p2, p3].filter(Boolean).join(" ");
  }

  return d.replaceAll(/(.{4})/g, "$1 ").trim();
}

/**
 * Month/year/CVV validation helpers.
 */
function isValidMonth(mm) {
  if (!/^\d{2}$/.test(mm)) return false;
  const n = Number(mm);
  return n >= 1 && n <= 12;
}

function isValidYear(yy) {
  return /^\d{2}$/.test(yy);
}

/**
 * Expiry must not be in the past.
 * We interpret YY as 20YY (e.g., 26 => 2026).
 */
function expiryNotPast(mm, yy) {
  if (!isValidMonth(mm) || !isValidYear(yy)) return false;

  const month = Number(mm);
  const year = 2000 + Number(yy);

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1;

  if (year < curYear) return false;
  if (year === curYear && month < curMonth) return false;
  return true;
}

function isValidCvv(brand, cvvDigits) {
  if (!/^\d+$/.test(cvvDigits)) return false;
  if (brand === "AMEX") return cvvDigits.length === 4;
  return cvvDigits.length === 3;
}

/**
 * UI polish: card glow changes by brand.
 */
function brandGlow(brand) {
  switch (brand) {
    case "VISA":
      return { glow: "rgba(37, 99, 235, 0.35)", glow2: "rgba(29, 78, 216, 0.22)" };
    case "MASTERCARD":
      return { glow: "rgba(245, 158, 11, 0.38)", glow2: "rgba(239, 68, 68, 0.22)" };
    case "AMEX":
      return { glow: "rgba(6, 182, 212, 0.35)", glow2: "rgba(59, 130, 246, 0.22)" };
    case "DISCOVER":
      return { glow: "rgba(251, 146, 60, 0.40)", glow2: "rgba(234, 179, 8, 0.22)" };
    default:
      return { glow: "rgba(148, 163, 184, 0.35)", glow2: "rgba(100, 116, 139, 0.20)" };
  }
}

/**
 * normalizeOrigin()
 *
 * Security: the caller provides returnOrigin via query string.
 * We normalize it to a strict origin like "https://example.com".
 * If invalid, return "" so we refuse to postMessage.
 */
function normalizeOrigin(maybeOriginOrUrl) {
  const raw = (maybeOriginOrUrl || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw).origin;
  } catch {
    return "";
  }
}

/**
 * encryptWithPaytrace()
 *
 * We use PayTrace's encryptValue() function. Different PayTrace builds may accept
 * slightly different signatures; we attempt the common ones safely.
 */
function encryptWithPaytrace(pt, plainValue, labelForErrors) {
  if (!pt) throw new Error("PayTrace is not initialized yet.");
  if (plainValue == null || String(plainValue).trim() === "") {
    throw new Error(`Missing ${labelForErrors}.`);
  }

  const value = String(plainValue);

  let encrypted;
  try {
    // Prefer the most common/current signature first.
    // Choose based on the function's declared parameter count to avoid exception-driven flow.
    if (typeof pt.encryptValue !== "function") {
      throw new TypeError("PayTrace encryptValue is not a function.");
    }

    if (pt.encryptValue.length >= 2) {
      // encryptValue(label, value)
      encrypted = pt.encryptValue(labelForErrors, value);
    } else {
      // encryptValue(value)
      encrypted = pt.encryptValue(value);
    }

    // Some implementations accept an object signature; use it only if the above didn't work.
    if ((encrypted == null || encrypted === "") && pt.encryptValue.length === 1) {
      encrypted = pt.encryptValue({ value });
    }
  } catch (err) {
    // Handle the exception (don’t silently swallow it).
    const message =
      err instanceof Error ? err.message : "Unknown error during encryption";
    throw new Error(`PayTrace encryption failed for ${labelForErrors}: ${message}`);
  }

  if (typeof encrypted !== "string" || encrypted.trim() === "") {
    throw new Error(
      `PayTrace encryptValue did not return an encrypted string for ${labelForErrors}.`
    );
  }

  // Safety check: reject anything that still looks like a raw PAN.
  if (
    String(labelForErrors).toLowerCase().includes("card") &&
    /^[\d\s-]{12,25}$/.test(encrypted)
  ) {
    throw new Error("Encrypted card value still looks like a PAN. Aborting.");
  }

  return encrypted.trim();
}

/**
 * usePaytraceInit()
 *
 * This hook owns the PayTrace initialization step:
 * - Validate that required query params exist (quote + returnOrigin)
 * - Load required scripts (jQuery + PayTrace E2EE)
 * - Fetch the public key from our API
 * - Call pt.setKey(keyText)
 */
function usePaytraceInit({ quote, returnOrigin }) {
  const paytraceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState("");

  useEffect(() => {
    // Guard: if caller didn't pass required params, don't init.
    if (!quote || !returnOrigin) {
      setReady(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function waitForPaytrace(timeoutMs = 3000) {
      const start = Date.now();
      while (!window.paytrace) {
        if (Date.now() - start > timeoutMs) return null;
        await new Promise((r) => setTimeout(r, 25));
      }
      return window.paytrace;
    }

    async function init() {
      try {
        setLoading(true);
        setInitError("");

        // PayTrace E2EE depends on jQuery in this integration.
        await loadScript("https://code.jquery.com/jquery-1.11.1.min.js");

        // Load PayTrace E2EE library.
        await loadScript(PAYTRACE_E2EE_SRC);

        // Wait for window.paytrace.
        const pt = await waitForPaytrace();
        if (!pt) throw new Error("PayTrace library loaded, but window.paytrace was not found.");

        // Fetch the public key (server returns PEM text).
        const r = await fetch(
          `${process.env.REACT_APP_API_URL}/pnapi/policy/${encodeURIComponent(quote)}/billing/key`,
          {
            headers: { "x-api-key": process.env.REACT_APP_X_API_KEY },
          }
        );

        if (!r.ok) throw new Error(`Key request failed (${r.status}).`);
        const data = await r.json();
        const keyText = data?.public_key;
        if (!keyText || typeof keyText !== "string") throw new Error("Key endpoint did not return public_key text.");

        // Store PayTrace instance and key.
        pt.setKey(keyText);
        paytraceRef.current = pt;

        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) {
          paytraceRef.current = null;
          setReady(false);
          setInitError(e?.message || "Failed to initialize tokenization page.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [quote, returnOrigin]);

  return { paytraceRef, loading, ready, initError, setInitError };
}

export default function TokenizeCardPage() {
  /**
   * The caller should open this page like:
   *   /tokenize?quote=123&returnOrigin=https://caller-site.com
   *
   * - quote: lets the caller match the response to the correct quote
   * - returnOrigin: used as the strict targetOrigin for postMessage
   */
  const search = window.location.search;

  const quote = useMemo(() => (new URLSearchParams(search).get("quote") || "").trim(), [search]);
  const returnOriginRaw = useMemo(
    () => (new URLSearchParams(search).get("returnOrigin") || "").trim(),
    [search]
  );
  const returnOrigin = useMemo(() => normalizeOrigin(returnOriginRaw), [returnOriginRaw]);

  // Initialize PayTrace (script load + key load).
  const { paytraceRef, loading, ready, initError, setInitError } = usePaytraceInit({
    quote,
    returnOrigin,
  });

  /**
   * Inputs (user types here).
   * Important: these values are NEVER sent back to the caller; we only send encrypted fields.
   */
  const [cardNumber, setCardNumber] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");

  /**
   * Derived UI + validation values.
   */
  const cardDigits = useMemo(() => onlyDigits(cardNumber), [cardNumber]);
  const brand = useMemo(() => detectCardBrand(cardDigits), [cardDigits]);
  const { glow, glow2 } = useMemo(() => brandGlow(brand), [brand]);

  const panComplete = useMemo(() => panLengthOk(brand, cardDigits.length), [brand, cardDigits.length]);
  const panValid = useMemo(() => (panComplete ? luhnCheck(cardDigits) : false), [panComplete, cardDigits]);

  const nameValid = useMemo(() => cardholderName.trim().length >= 2, [cardholderName]);
  const monthValid = useMemo(() => isValidMonth(expMonth), [expMonth]);
  const yearValid = useMemo(() => isValidYear(expYear), [expYear]);
  const expiryValid = useMemo(() => expiryNotPast(expMonth, expYear), [expMonth, expYear]);
  const cvvValid = useMemo(() => isValidCvv(brand, cvv), [brand, cvv]);

  /**
   * This page only "works" if it was launched correctly.
   * If quote or returnOrigin is missing/invalid, we disable confirm.
   */
  const canPostBack = !!returnOrigin && !!quote;

  const canConfirm =
    ready &&
    !loading &&
    canPostBack &&
    nameValid &&
    panComplete &&
    panValid &&
    monthValid &&
    yearValid &&
    expiryValid &&
    cvvValid;

  /**
   * Determine how we were launched:
   * - Popup/new tab: window.opener exists
   * - Iframe/modal: window.parent exists (and is different than window)
   */
  const isPopup = useMemo(() => !!window.opener && !window.opener.closed, []);

  /**
   * postToCaller(message)
   *
   * We always send ONLY to returnOrigin (strict origin).
   * Never use "*" here.
   */
  const postToCaller = useCallback(
    (message) => {
      if (!returnOrigin) return;

      // If opened via window.open (popup tab)
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(message, returnOrigin);

        // Close the popup tab after sending message
        try {
          window.close();
        } catch {
          // Some browsers may block close — ignore safely
        }

        return;
      }

      // Otherwise assume iframe modal
      if (window.parent) {
        window.parent.postMessage(message, returnOrigin);
      }
    },
    [returnOrigin]
  );


  /**
   * CANCEL: Tell the caller the user cancelled and close popup if needed.
   */
  const onCancel = useCallback(() => {
    try {
      if (canPostBack) {
        postToCaller({
          type: "TOKENIZE_CANCELLED",
          payload: {
            quoteNumber: quote,
            createdAtUtc: new Date().toISOString(),
          },
          quoteNumber: quote,
        });
      }
    } finally {
      if (isPopup) window.close();
    }
  }, [canPostBack, isPopup, postToCaller, quote]);

  /**
   * CONFIRM: Encrypt and post message back to caller.
   * - Uses PayTrace encryptValue (client-side E2EE).
   * - Sends ONLY encrypted values back.
   */
  const handleConfirmClick = useCallback(async () => {
    try {
      setInitError("");

      const pt = paytraceRef.current;
      if (!pt) throw new Error("PayTrace is not ready yet.");

      // Encrypt raw values (no spaces).
      const rawCard = (cardNumber || "").replaceAll(/\s+/g, "");
      const rawCsc = (cvv || "").trim();

      const encNum = encryptWithPaytrace(pt, rawCard, "card_number");
      const encCsc = encryptWithPaytrace(pt, rawCsc, "csc");

      const payload = {
        quoteNumber: quote,
        encrypted_number: encNum,
        encrypted_csc: encCsc,
        exp_month: (expMonth || "").trim(),
        exp_year: (expYear || "").trim(),
        cardholder_name: (cardholderName || "").trim(),
        createdAtUtc: new Date().toISOString(),
      };

      postToCaller({ type: "TOKENIZE_RESULT", payload, quoteNumber: quote });

      // Optional cleanup: clear the sensitive inputs after success.
      setCardNumber("");
      setCardholderName("");
      setExpMonth("");
      setExpYear("");
      setCvv("");
    } catch (e) {
      setInitError(e?.message || "Encryption failed.");
    }
  }, [cardNumber, cardholderName, cvv, expMonth, expYear, paytraceRef, postToCaller, quote, setInitError]);

  /**
   * Input handlers:
   * - Card number: we format it as the user types.
   * - Month/year/cvv: digits only.
   */
  function handleCardChange(e) {
    const digits = onlyDigits(e.target.value);
    const detected = detectCardBrand(digits);
    const formatted = formatCardNumberByBrand(digits, detected);
    setCardNumber(formatted);
  }

  function handleMonthChange(e) {
    setExpMonth(onlyDigits(e.target.value).slice(0, 2));
  }

  function handleMonthBlur() {
    // Example: user types "2" and tabs out -> store "02"
    if (expMonth.length === 1) setExpMonth(`0${expMonth}`);
  }

  function handleYearChange(e) {
    setExpYear(onlyDigits(e.target.value).slice(0, 2));
  }

  function handleCvvChange(e) {
    const maxLen = brand === "AMEX" ? 4 : 3;
    setCvv(onlyDigits(e.target.value).slice(0, maxLen));
  }

  /**
   * These booleans help us show red borders when a field is invalid.
   * We only mark invalid after user starts typing (so blank fields aren't red).
   */
  const cardInvalid = cardNumber.length > 0 && (!panComplete || (panComplete && !panValid));
  const nameInvalid = cardholderName.length > 0 && !nameValid;
  const monthInvalid = expMonth.length > 0 && !monthValid;
  const yearInvalid = expYear.length > 0 && !yearValid;
  const expiryInvalid = expMonth.length === 2 && expYear.length === 2 && !expiryValid;
  const cvvInvalid = cvv.length > 0 && !cvvValid;

  // UI: brand logo
  const showBrandLogo = cardDigits.length >= 2;
  const brandLogoSrc = BRAND_LOGO[brand] || BRAND_LOGO.CARD;

  // UI: values shown on the card illustration (never show real CVV digits on the card art)
  const expOnCard = expMonth.length === 2 && expYear.length === 2 ? `${expMonth}/${expYear}` : "MM/YY";
  let cvvMasked = brand === "AMEX" ? "1234" : "123";

  if (cvv.length > 0) {
    cvvMasked = "•".repeat(cvv.length);
  }

  return (
    <div className="pnc-modalRoot">
      {/* Component-scoped CSS so this page can be hosted standalone */}
      <style>{`
        :root{
          --bg: #f3f6fb;
          --panel: #ffffff;
          --text: #0f172a;
          --border: rgba(15, 23, 42, 0.12);
          --shadow: 0 20px 55px rgba(2, 6, 23, 0.22);
          --radius: 18px;
          --dangerBorder: rgba(185, 28, 28, 0.45);
        }

        .pnc-modalRoot{
          min-height: 100vh;
          background: radial-gradient(1100px 600px at 30% 20%, #ffffff, var(--bg));
          display: grid;
          place-items: center;
          padding: 18px;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          color: var(--text);
        }

        .pnc-dialog{
          position: relative;
          width: min(980px, 100%);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          border: 1px solid var(--border);
          overflow: hidden;
          background: var(--panel);
          display: grid;
          grid-template-columns: 420px 1fr;
          min-height: 520px;
        }

        @media (max-width: 760px){
          .pnc-dialog{ grid-template-columns: 1fr; }
        }

        .pnc-top{
          position: absolute;
          left: 18px;
          top: 14px;
          display: flex;
          align-items: center;
          z-index: 2;
        }

        .pnc-logo{
          height: 56px;
          width: auto;
          display: block;
        }

        .pnc-left{
          background: linear-gradient(180deg, #f7f9fd, #eef3fb);
          padding: 72px 18px 18px 18px;
          display: grid;
          place-items: center;
        }

        .cardStack{
          position: relative;
          width: min(360px, 92%);
          aspect-ratio: 16 / 10;
        }

        .cardBase{
          position: absolute;
          inset: 0;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.24);
          overflow: hidden;
        }

        .cardBack{
          transform: translate(32px, 24px);
          background: linear-gradient(135deg, #2b6fd6, #0f4fb3);
          opacity: 0.95;
          box-shadow: 0 18px 50px rgba(2, 6, 23, 0.18);
        }

        .magStripe{
          position: absolute;
          left: 0;
          right: 0;
          top: 22px;
          height: 46px;
          background: rgba(2, 6, 23, 0.62);
        }

        .signature{
          position: absolute;
          left: 16px;
          right: 96px;
          top: 82px;
          height: 22px;
          border-radius: 6px;
          background: rgba(255,255,255,0.74);
        }

        .cvvBox{
          position: absolute;
          right: 16px;
          top: 82px;
          width: 70px;
          height: 22px;
          border-radius: 6px;
          background: rgba(255,255,255,0.85);
          display: grid;
          place-items: center;
          font-weight: 800;
          letter-spacing: 0.14em;
          color: rgba(2,6,23,0.90);
          font-size: 12px;
        }

        .cardFront{
          background: linear-gradient(135deg, #1d7ce8, #155cc9);
          box-shadow:
            0 20px 55px rgba(2, 6, 23, 0.18),
            0 0 0 1px rgba(255,255,255,0.18),
            0 0 38px ${glow},
            0 0 82px ${glow2};
        }

        .shine{
          position: absolute;
          inset: -40% -40% auto auto;
          width: 80%;
          height: 80%;
          background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), transparent 55%);
          transform: rotate(18deg);
        }

        .chip{
          position: absolute;
          left: 18px;
          top: 18px;
          width: 52px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(145deg, #f6d365, #fda085);
          box-shadow: inset 0 0 0 2px rgba(2,6,23,0.15);
        }

        .contactless{
          position: absolute;
          right: 18px;
          top: 18px;
          width: 28px;
          height: 28px;
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.7);
          opacity: 0.7;
        }

        .cardNumberArt{
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: 62px;
          font-size: 20px;
          letter-spacing: 0.14em;
          color: rgba(255,255,255,0.92);
          text-shadow: 0 2px 10px rgba(2,6,23,0.22);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .expArt{
          position: absolute;
          right: 18px;
          bottom: 60px;
          font-size: 12px;
          letter-spacing: 0.12em;
          color: rgba(255,255,255,0.88);
          text-shadow: 0 2px 10px rgba(2,6,23,0.18);
        }

        .cardMeta{
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          letter-spacing: 0.08em;
          color: rgba(255,255,255,0.85);
        }

        .brandBadge{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 7px 10px;
          border-radius: 999px;
          background: rgba(255,255,255,0.16);
          border: 1px solid rgba(255,255,255,0.18);
        }

        .brandImgOnCard{
          height: 16px;
          width: auto;
          display: block;
          filter: brightness(1.05);
        }

        .pnc-right{
          padding: 72px 26px 22px 26px;
          background: #ffffff;
        }

        .title{
          margin: 0 0 14px 0;
          font-size: 30px;
          letter-spacing: -0.02em;
        }

        .banner{
          border-radius: 12px;
          padding: 10px 12px;
          margin: 0 0 12px 0;
          border: 1px solid rgba(15,23,42,0.10);
          font-size: 13px;
        }
        .banner.info{ background: #eff6ff; color: #1d4ed8; border-color: rgba(29,78,216,0.22); }
        .banner.err{ background: #fef2f2; color: #b91c1c; border-color: rgba(185,28,28,0.22); }

        .fieldWrap{
          position: relative;
          display: grid;
          gap: 10px;
        }

        .input{
          width: 100%;
          height: 46px;
          border-radius: 10px;
          border: 1px solid rgba(15,23,42,0.18);
          padding: 12px 12px;
          outline: none;
          transition: box-shadow 120ms ease, border-color 120ms ease;
          font-size: 14px;
        }

        .input::placeholder{
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-size: 12px;
        }

        .input:focus{
          border-color: rgba(29,124,232,0.9);
          box-shadow: 0 0 0 4px rgba(29,124,232,0.16);
        }

        .input.invalid{
          border-color: var(--dangerBorder);
          box-shadow: 0 0 0 4px rgba(185, 28, 28, 0.12);
        }

        .brandInline{
          position: absolute;
          right: 10px;
          top: 10px;
          height: 26px;
          padding: 0 10px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          background: #f1f5f9;
          border: 1px solid rgba(15,23,42,0.10);
          pointer-events: none;
        }

        .brandImg{
          height: 16px;
          width: auto;
          display: block;
        }

        .row3{
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
        }

        @media (max-width: 520px){
          .pnc-right{ padding-left: 16px; padding-right: 16px; }
          .row3{ grid-template-columns: 1fr; }
          .pnc-left{ padding-top: 88px; }
        }

        .actions{
          display: grid;
          grid-template-columns: 1fr 130px;
          gap: 12px;
          margin-top: 12px;
          align-items: center;
        }

        .btn{
          height: 46px;
          border-radius: 10px;
          border: 1px solid rgba(15,23,42,0.18);
          cursor: pointer;
          font-weight: 900;
          letter-spacing: 0.04em;
          transition: transform 80ms ease, opacity 120ms ease, box-shadow 120ms ease;
        }

        .btn.primary{
          background: linear-gradient(135deg, #1d7ce8, #155cc9);
          color: white;
          border-color: rgba(29,124,232,0.25);
          box-shadow: 0 12px 20px rgba(29,124,232,0.18);
        }
        .btn.primary:hover{ transform: translateY(-1px); }
        .btn.primary:active{ transform: translateY(0px); }
        .btn.primary:disabled{
          opacity: 0.50;
          cursor: not-allowed;
          box-shadow: none;
          transform: none;
        }

        .btn.ghost{
          background: transparent;
          color: #2563eb;
        }
      `}</style>

      <dialog className="pnc-dialog" aria-label="Payment Details">
        <div className="pnc-top">
          <PncLogo className="pnc-logo" aria-label="Producers National" />
        </div>

        {/* Left: decorative card preview */}
        <div className="pnc-left" aria-hidden="true">
          <div className="cardStack">
            <div className="cardBase cardBack">
              <div className="magStripe" />
              <div className="signature" />
              <div className="cvvBox">{cvvMasked}</div>
            </div>

            <div className="cardBase cardFront">
              <div className="shine" />
              <div className="chip" />
              <div className="contactless" />
              <div className="cardNumberArt">
                {cardNumber || (brand === "AMEX" ? "1234 567890 12345" : "1234 5678 9012 3456")}
              </div>

              <div className="expArt">{expOnCard}</div>

              <div className="cardMeta">
                <span>{(cardholderName || "CARDHOLDER NAME").toUpperCase()}</span>
                <span className="brandBadge" aria-label={brand}>
                  <img className="brandImgOnCard" src={brandLogoSrc} alt={brand} />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: inputs */}
        <div className="pnc-right">
          <h1 className="title">Payment Details</h1>

          {!!initError && <div className="banner err">{initError}</div>}

          {/* If the user navigated directly, show a clear, non-technical message */}
          {(!quote || !returnOriginRaw) && (
            <div className="banner err">
              Missing required query params. Open with <strong>?quote=####&amp;returnOrigin=https://parent-site</strong>
            </div>
          )}

          {(!!returnOriginRaw && !returnOrigin) && (
            <div className="banner err">
              Invalid returnOrigin. Must be a full origin or URL like <strong>https://parent-site</strong>
            </div>
          )}

          {/* We do NOT submit this form anywhere. Confirm button does encryption + postMessage. */}
          <form onSubmit={(e) => e.preventDefault()} noValidate>
            <div className="fieldWrap">
              <input
                className={`input ${cardInvalid ? "invalid" : ""}`}
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder="CARD NUMBER"
                value={cardNumber}
                onChange={handleCardChange}
                aria-invalid={cardInvalid ? "true" : "false"}
              />

              {showBrandLogo && (
                <div className="brandInline" aria-label={brand}>
                  <img className="brandImg" src={brandLogoSrc} alt={brand} />
                </div>
              )}
            </div>

            <div className="fieldWrap" style={{ marginTop: 10 }}>
              <input
                className={`input ${nameInvalid ? "invalid" : ""}`}
                autoComplete="cc-name"
                placeholder="CARDHOLDER NAME"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                aria-invalid={nameInvalid ? "true" : "false"}
              />
            </div>

            <div className="row3" style={{ marginTop: 10 }}>
              <div className="fieldWrap">
                <input
                  className={`input ${monthInvalid || expiryInvalid ? "invalid" : ""}`}
                  inputMode="numeric"
                  autoComplete="cc-exp-month"
                  placeholder="MM"
                  value={expMonth}
                  onChange={handleMonthChange}
                  onBlur={handleMonthBlur}
                  aria-invalid={monthInvalid || expiryInvalid ? "true" : "false"}
                />
              </div>

              <div className="fieldWrap">
                <input
                  className={`input ${yearInvalid || expiryInvalid ? "invalid" : ""}`}
                  inputMode="numeric"
                  autoComplete="cc-exp-year"
                  placeholder="YY"
                  value={expYear}
                  onChange={handleYearChange}
                  aria-invalid={yearInvalid || expiryInvalid ? "true" : "false"}
                />
              </div>

              <div className="fieldWrap">
                <input
                  className={`input ${cvvInvalid ? "invalid" : ""}`}
                  type="password"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder={brand === "AMEX" ? "CID" : "CVV"}
                  value={cvv}
                  onChange={handleCvvChange}
                  aria-invalid={cvvInvalid ? "true" : "false"}
                />
              </div>
            </div>

            <div className="actions">
              <button type="button" className="btn primary" disabled={!canConfirm} onClick={handleConfirmClick}>
                CONFIRM
              </button>

              <button type="button" className="btn ghost" onClick={onCancel}>
                CANCEL
              </button>
            </div>

            {!canPostBack && (
              <div className="banner info" style={{ marginTop: 12 }}>
                This page must be launched from the caller so it can return the encrypted values safely.
              </div>
            )}
          </form>
        </div>
      </dialog>
    </div>
  );
}
